import React, { useState, useEffect, useRef } from 'react';
import { BOARD_SIZE } from '../../constants';
import player1Image from '../../assets/images/player_1.png';
import player2Image from '../../assets/images/player_2.png';

const CELL_SIZE = 32;
const ANIMATION_TIMINGS = {
  hints: 1000,
  wall: 700,
  bump: 500,
  pause: 1000 // black wall stays after bump
};

function PlacingWalls() {
  // Initial board state with one black stone
  const [board] = useState(() => {
    const board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
    board[3][3] = 'black';
    return board;
  });
  const stone = { row: 3, col: 3 };

  // Animation phase: 'hints', 'wall', 'bump', 'pause'
  const [phase, setPhase] = useState('hints');
  const [bump, setBump] = useState(false);
  const timeoutRef = useRef();

  // Animation loop
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (phase === 'hints') {
      timeoutRef.current = setTimeout(() => setPhase('wall'), ANIMATION_TIMINGS.hints);
    } else if (phase === 'wall') {
      timeoutRef.current = setTimeout(() => {
        setBump(true);
        setPhase('bump');
      }, ANIMATION_TIMINGS.wall);
    } else if (phase === 'bump') {
      timeoutRef.current = setTimeout(() => {
        setBump(false);
        setPhase('pause');
      }, ANIMATION_TIMINGS.bump);
    } else if (phase === 'pause') {
      timeoutRef.current = setTimeout(() => {
        setPhase('hints');
      }, ANIMATION_TIMINGS.pause);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [phase]);

  // For bump animation: reset bump state when phase changes
  useEffect(() => {
    if (phase !== 'bump') setBump(false);
  }, [phase]);

  return (
    <div className="flex justify-center w-full">
      <div className="flex items-center justify-between w-full max-w-2xl">
        {/* Player 1 (Black) */}
        <div className="flex flex-col items-center opacity-0 sm:opacity-100">
          <img src={player1Image} alt="Player 1" className="w-24 h-24 object-contain" />
        </div>

        {/* Board */}
        <div className="relative rounded-lg p-4" style={{ backgroundColor: '#e1c083' }}>
          <div
            className="grid gap-0 relative"
            style={{
              gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))`,
              width: `${BOARD_SIZE * CELL_SIZE}px`,
              height: `${BOARD_SIZE * CELL_SIZE}px`
            }}
          >
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const isStoneCell = rowIndex === stone.row && colIndex === stone.col;
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className="relative w-8 h-8"
                  >
                    {/* Grid lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      {rowIndex < BOARD_SIZE - 1 && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#99804a]" />
                      )}
                      {colIndex < BOARD_SIZE - 1 && (
                        <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-[#99804a]" />
                      )}
                    </div>

                    {/* Stone */}
                    {cell && (
                      <div
                        className={`absolute mr-[1px] -ml-[1px] mb-[1px] -mt-[1px] inset-2 rounded-full bg-black shadow-sm shadow-white transition-transform duration-500 ${bump ? 'animate-stone-bump' : ''}`}
                      />
                    )}

                    {/* Walls tightly around the stone */}
                    {isStoneCell && (
                      <>
                        {/* Top wall (hinted) */}
                        <div className={`
                          transition-all ${phase === 'hints' ? 'opacity-70' : 'opacity-0'}
                          -ml-0.5 -mt-0.5 absolute left-0 right-0 top-0 h-1 bg-blue-800 duration-500
                        `} />
                        {/* Bottom wall (hinted) */}
                        <div className={`
                          transition-all ${phase === 'hints' ? 'opacity-70' : 'opacity-0'}
                          -ml-0.5 absolute left-0 right-0 bottom-0 h-1 bg-blue-800 duration-500
                        `} />
                        {/* Right wall (hinted) */}
                        <div className={`
                          transition-all ${phase === 'hints' ? 'opacity-70' : 'opacity-0'}
                          -ml-0.5 mb-0.5 absolute right-0 top-0 bottom-0 w-1 bg-blue-800 duration-500
                        `} />
                        {/* Left wall (hinted) */}
                        < div className={`
                          transition-all ${phase === 'hints' ? 'opacity-70' : 'opacity-0'}
                          -ml-0.5 mb-0.5 absolute left-0 top-0 bottom-0 w-1 bg-blue-800 duration-500
                        `} />
                        {/* Left wall (real, pop in, stays during bump and pause) */}
                        {(phase === 'wall' || phase === 'bump' || phase === 'pause') && (
                          <div className="-ml-1 mb-0.5 absolute left-0 top-0 bottom-0 w-1.5 bg-black transition-all duration-300 animate-wall-pop" />
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Player 2 (White) */}
        <div className="flex flex-col items-center opacity-0 sm:opacity-100">
          <img src={player2Image} alt="Player 2" className="w-24 h-24 object-contain opacity-30" />
        </div>
      </div>
      {/* Custom keyframes for bump and wall pop */}
      <style>{`
        @keyframes stone-bump {
                          0 % { transform: translateX(0); }
          30% {transform: translateX(-7px); }
                        40% {transform: translateX(-4px); }
                        70% {transform: translateX(-7px); }
                        100% {transform: translateX(0); }
        }
                        .animate-stone-bump {
                          animation: stone-bump 0.5s cubic-bezier(0.9,.9,.9,.9) 1;
        }
                        @keyframes wall-pop {
                          0 % { transform: scaleY(0.2); opacity: 0; }
          60% {transform: scaleY(1.1); opacity: 1; }
                        100% {transform: scaleY(1); opacity: 1; }
        }
                        .animate-wall-pop {
                          animation: wall-pop 0.3s cubic-bezier(.4,1.6,.6,1) 1;
        }
      `}</style>
    </div>
  );
}

export default PlacingWalls; 
